import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { groupBy, orderBy } from 'lodash';
import {
  Box,
  Button,
  type ButtonProps,
  Dialog,
  Flex,
  GridAlpha,
  Heading,
  MenuButton,
  MultiSelect,
  Select,
  SrOnly,
  StickyBar,
  Text,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import {
  useApplication,
  useApplicationAvailableActions,
  useApplicationPagePermissions,
  useApplicationRequirements,
  useDismissManualInspection,
  useFeatureFlags,
  useIsNewLayoutEnabled,
} from '../../data';
import {
  ChevronDownOutlineIcon,
  ChevronUpOutlineIcon,
  CompareArrowsOutlineIcon,
  DocumentAlertHifiIcon,
  DocumentCheckOutlineIcon,
  DownloadOutlineIcon,
  FilterOutlineIcon,
  MoreVertOutlineIcon,
  PlusOutlineIcon,
  WarningOutlineIcon,
} from '@applyboard/ui-icons';
import { useTranslation } from '@applyboard/ui-utils';
import {
  ApplicationActionName,
  ApplicationRequirement,
  RequirementState,
  requirementStates,
  RequirementStatus,
  requirementStatuses,
  RequirementApplicationStage,
  type RequirementApplicationStage as ApplicationStage,
} from '~/types';
import { getRequirementStatusInfo } from '~/components/RequirementCard';

import {
  ApplicationRequirementCard,
  ApplicationRequirementCardProps,
  ApplicationRequirementPreviewPanel,
} from './ApplicationRequirementCard';
import { assert } from '~/utils/assert';
import { AddRequirementsButton } from '../AddRequirementsButton';
import { useRequirementListEventTracker } from '../../utils/eventTracking';
import { HurryUpDialog } from './HurryUpDialog';
import { GLOBAL_TOP_BAR_HEIGHT, GUTTER_WIDTH_TOKEN, MINI_HEADER_HEIGHT } from '../../consts';
import { LegacyCompareRequirementsModal } from '@applyboard/ab-application-legacy';

export function RequirementList({
  requirementClassifications,
}: {
  requirementClassifications: ApplicationRequirement['classification'][];
}) {
  const { t } = useTranslation('RequirementList');
  const desktopPreviewPanelId = useId();
  const requirementListId = useId();
  const { trackEvent } = useRequirementListEventTracker();

  const expandButtonRef = useRef<HTMLButtonElement | null>(null);
  const desktopCollapseButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileCollapseButtonRef = useRef<HTMLButtonElement | null>(null);

  const { permissions } = useApplicationPagePermissions();

  const { featureFlags } = useFeatureFlags();
  const showModernFeatures =
    featureFlags['application_apr_admin_features'] || featureFlags['application_apr_actions_tab'];

  const showDeactivated = !!(
    (permissions.list_all_requirements || permissions.read_past_stages_optional_requirements) &&
    showModernFeatures
  );

  const isAdminRequirementsList = !requirementClassifications.includes('applicant_requirement');

  const { applicationRequirements: allApplicationRequirements } = useApplicationRequirements();
  const applicationRequirements = allApplicationRequirements.filter(
    requirement =>
      requirementClassifications.includes(requirement.classification) &&
      (showDeactivated || requirement.status !== 'inactive'),
  );

  const canGroupByStage =
    showModernFeatures &&
    applicationRequirements.some(requirement => !!requirement.applicationStage);
  const defaultGroupByAttribute = canGroupByStage ? 'stage' : 'status';
  const [groupByAttribute, setGroupByAttribute] = useState<'status' | 'stage'>(
    defaultGroupByAttribute,
  );

  const { application_id: applicationId, requirement_id: selectedRequirementId } = useParams<{
    application_id: string;
    requirement_id?: string;
  }>();

  const selectedRequirement = applicationRequirements.find(
    requirement => requirement.id === selectedRequirementId,
  );

  const { selectedFilters, handleFilterChange, filterOptions } = useFilters({
    applicationId,
    applicationRequirements,
    selectedRequirement,
    selectedRequirementId,
  });

  const filteredApplicationRequirements = filterRequirements(
    applicationRequirements,
    selectedFilters,
  );

  const [openRequirement, setOpenRequirement] = useState<null | Pick<
    ApplicationRequirement,
    'status' | 'applicationStage' | 'id'
  >>(null);

  const groupedApplicationRequirements = groupRequirementsByStatus({
    openRequirement,
    requirements: filteredApplicationRequirements,
    groupByAttribute,
  });

  const requirementStatusesToDisplay = filterOptions.requirementStatus.filter(
    status => groupedApplicationRequirements[status],
  );

  const requirementGroupings =
    groupByAttribute === 'status' ? requirementStatusesToDisplay : getRequirementStagesToDisplay();

  const handleRequirementCollapse = () => {
    expandButtonRef.current?.focus();
  };

  const [submittedRequirement, setSubmittedRequirement] = useState('');
  const [isHurryUpDialogOpen, setIsHurryUpDialogOpen] = useState(false);
  const [isCompareRequirementsModalOpen, setIsCompareRequirementsModalOpen] = useState(false);

  const handleSubmissionSuccess = (requirementId: string) => {
    setSubmittedRequirement(requirementId);
    setIsHurryUpDialogOpen(true);
  };

  const isNewLayoutEnabled = useIsNewLayoutEnabled();

  // The display of the close button needs to complete before we can set the focus
  // eslint-disable-next-line no-restricted-syntax
  useEffect(() => {
    if (selectedRequirementId) {
      mobileCollapseButtonRef.current?.focus();
      desktopCollapseButtonRef.current?.focus();
    }
  }, [selectedRequirementId]);

  return (
    <>
      <section
        css={theme => ({
          position: 'relative',
          marginTop: isNewLayoutEnabled ? `-${theme.spacing[GUTTER_WIDTH_TOKEN]}` : undefined,
        })}
        aria-labelledby={requirementListId}
      >
        <GridAlpha
          areas={{ xs: ['list'], lg: ['list preview'] }}
          columnGap={{ lg: selectedRequirement ? 6 : 0 }}
          columns={{ xs: '100%', lg: '320px auto' }}
          pt={isNewLayoutEnabled ? 0 : 6}
        >
          <GridAlpha.Item
            columnStart="list"
            columnEnd={!selectedRequirementId ? 'preview' : undefined}
          >
            <div
              css={{
                height: isNewLayoutEnabled ? undefined : '100%',
                maxHeight: isNewLayoutEnabled ? undefined : 'calc(100vh)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <SrOnly>
                <Heading id={requirementListId} level={2} variant="titleL">
                  {isAdminRequirementsList ? t('titleAdmin') : t('title')}
                </Heading>
              </SrOnly>
              <StickyBar
                sticky={isNewLayoutEnabled}
                top={GLOBAL_TOP_BAR_HEIGHT + MINI_HEADER_HEIGHT}
                py={GUTTER_WIDTH_TOKEN}
                wings={5}
              >
                <Flex direction="row" justify={showModernFeatures ? 'between' : 'end'} gap={4} wrap>
                  {!showModernFeatures && (
                    <Flex.Item>
                      <RequirementFilter
                        filterOptions={filterOptions}
                        selectedFilters={selectedFilters}
                        onChange={handleFilterChange}
                      />
                    </Flex.Item>
                  )}
                  {showModernFeatures && (
                    <>
                      <Flex.Item grow={1}>
                        <Flex
                          direction={{
                            xs: 'column',
                            sm: selectedRequirementId ? 'column' : 'row',
                          }}
                          justify="between"
                          gap={4}
                          wrap
                        >
                          <Flex.Item basis={{ sm: selectedRequirementId ? undefined : 320 }}>
                            {filteredApplicationRequirements.some(
                              requirement => !!requirement.applicationStage,
                            ) && (
                              <Select
                                aria-label="View requirements by"
                                appearance="styled"
                                value={groupByAttribute}
                                onChange={value => {
                                  assert(
                                    value === 'status' || value === 'stage',
                                    'Invalid groupBy value',
                                  );
                                  setGroupByAttribute(value);
                                  trackEvent({
                                    event: `Clicked view by ${value === 'status' ? 'requirement status' : 'application stage'}`,
                                  });
                                }}
                                placeholder=""
                                placeholderVariant="value"
                              >
                                <Select.Option
                                  value="stage"
                                  label={t('viewRequirementsBy.stage')}
                                />
                                <Select.Option
                                  value="status"
                                  label={t('viewRequirementsBy.status')}
                                />
                              </Select>
                            )}
                          </Flex.Item>
                        </Flex>
                      </Flex.Item>
                      <div
                        css={{
                          [`@media (min-width: ${isNewLayoutEnabled ? 1721 : 1280}px)`]: {
                            display: 'none',
                          },
                        }}
                      >
                        <AdminActions
                          filterOptions={filterOptions}
                          isCompact
                          onFilterChange={handleFilterChange}
                          onOpenCompareDialog={() => setIsCompareRequirementsModalOpen(true)}
                          selectedFilters={selectedFilters}
                          requirementClassifications={requirementClassifications}
                        />
                      </div>
                      <div
                        css={{
                          [`@media (max-width: ${isNewLayoutEnabled ? 1720 : 1279}px)`]: {
                            display: 'none',
                          },
                          flexGrow: 1,
                        }}
                      >
                        <AdminActions
                          filterOptions={filterOptions}
                          requirementClassifications={requirementClassifications}
                          isCompact={!!selectedRequirementId}
                          onFilterChange={handleFilterChange}
                          onOpenCompareDialog={() => setIsCompareRequirementsModalOpen(true)}
                          selectedFilters={selectedFilters}
                        />
                      </div>
                    </>
                  )}
                </Flex>
              </StickyBar>
              <div
                css={isNewLayoutEnabled ? {} : { flex: 1, overflowY: 'auto', position: 'relative' }}
              >
                <Flex direction="column" pt={4} gap={2}>
                  {groupByAttribute === 'status' &&
                    requirementGroupings.map(key => {
                      const status = RequirementStatus.parse(key);
                      const requirementStatusLabel =
                        isAdminRequirementsList && status === 'missing'
                          ? t('requirementStatus.missingAdmin')
                          : t(`requirementStatus.${status}`);
                      return (
                        <RequirementGrouping
                          key={key}
                          leadIcon={
                            getRequirementStatusInfo({
                              requirement: {
                                status,
                              },
                            }).Icon
                          }
                          triggerText={`${requirementStatusLabel} (${groupedApplicationRequirements[status]?.length || '0'})`}
                        >
                          <GroupedApplicationRequirementCards
                            requirements={groupedApplicationRequirements[status]}
                            selectedRequirementId={selectedRequirementId}
                            expandButtonRef={expandButtonRef}
                            collapseButtonRef={mobileCollapseButtonRef}
                            desktopPreviewPanelId={desktopPreviewPanelId}
                            handleRequirementCollapse={handleRequirementCollapse}
                            setOpenRequirement={setOpenRequirement}
                            onSubmissionSuccess={handleSubmissionSuccess}
                          />
                        </RequirementGrouping>
                      );
                    })}

                  {groupByAttribute === 'stage' &&
                    requirementGroupings.map(key => {
                      const stage = RequirementApplicationStage.parse(key);
                      const requirements = groupedApplicationRequirements[stage];
                      return (
                        <RequirementGrouping
                          key={key}
                          triggerText={`${t(
                            `requirementApplicationStage.${stage}`,
                          )} (${requirements?.length || '0'})`}
                        >
                          <ApplicationStageRequirementGrouping
                            requirements={requirements}
                            statuses={filterOptions.requirementStatus}
                            stage={stage}
                            selectedRequirementId={selectedRequirementId}
                            expandButtonRef={expandButtonRef}
                            collapseButtonRef={mobileCollapseButtonRef}
                            desktopPreviewPanelId={desktopPreviewPanelId}
                            handleRequirementCollapse={handleRequirementCollapse}
                            setOpenRequirement={setOpenRequirement}
                            onSubmissionSuccess={handleSubmissionSuccess}
                          />
                        </RequirementGrouping>
                      );
                    })}
                </Flex>
              </div>
            </div>
          </GridAlpha.Item>
          <div
            css={{
              alignSelf: 'start',
              gridArea: selectedRequirement ? 'preview' : undefined,
              position: 'sticky',
              minWidth: 0,
              top: isNewLayoutEnabled ? GLOBAL_TOP_BAR_HEIGHT + MINI_HEADER_HEIGHT : 106, // 72px of topbar + 24px gap
            }}
          >
            <Box hideBelow="lg">
              <ApplicationRequirementPreviewPanel
                id={desktopPreviewPanelId}
                requirement={selectedRequirement}
                collapseButtonRef={desktopCollapseButtonRef}
                onCollapse={() => handleRequirementCollapse()}
                onSubmissionSuccess={handleSubmissionSuccess}
                onOpenChange={isOpen => {
                  setOpenRequirement(isOpen && selectedRequirement ? selectedRequirement : null);
                }}
                height={
                  isNewLayoutEnabled
                    ? `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT + MINI_HEADER_HEIGHT}px)`
                    : 'calc(100vh)'
                }
                pt={GUTTER_WIDTH_TOKEN}
              />
            </Box>
          </div>
        </GridAlpha>
      </section>
      {selectedRequirementId && !selectedRequirement && (
        <NotFoundModal applicationId={applicationId} />
      )}
      <HurryUpDialog
        requirementId={submittedRequirement}
        isOpen={isHurryUpDialogOpen}
        onOpenChange={setIsHurryUpDialogOpen}
      />
      {isCompareRequirementsModalOpen && (
        <LegacyCompareRequirementsModal
          open
          applicationId={applicationId}
          onClose={() => setIsCompareRequirementsModalOpen(false)}
          onUpdateClickedOnActions={() => {}}
          onUpdateClickedOnAppReq={() => {}}
          tab={
            requirementClassifications.includes('applicant_requirement')
              ? 'applicant requirements'
              : 'actions'
          }
        />
      )}
    </>
  );
}

function ApplicationStageRequirementGrouping({
  collapseButtonRef,
  desktopPreviewPanelId,
  expandButtonRef,
  handleRequirementCollapse,
  requirements,
  selectedRequirementId,
  setOpenRequirement,
  onSubmissionSuccess,
  stage,
  statuses,
}: GroupedApplicationRequirementCardsProps & {
  stage: ApplicationStage;
  statuses: RequirementStatus[];
}) {
  const { t } = useTranslation('RequirementList');

  if (!requirements) {
    return (
      <Text intent="secondary" contrast="mid">
        {t('noRequirements')}
      </Text>
    );
  }

  const isAdminRequirementsList = !requirements.some(
    req => req.classification === 'applicant_requirement',
  );

  return (
    <Flex direction="column" gap={3}>
      {statuses.map(status => {
        const requirementsForStatus = requirements.filter(
          requirement => requirement.status === status,
        );
        const requirementStatusLabel =
          isAdminRequirementsList && status === 'missing'
            ? t('requirementStatus.missingAdmin')
            : t(`requirementStatus.${status}`);
        if (!requirementsForStatus.length) {
          return null;
        }
        const headingId = `${stage}-${status}-grouping`;
        const Icon = getRequirementStatusInfo({
          requirement: { status },
        }).Icon;

        return (
          <section key={status} aria-labelledby={headingId}>
            <Flex direction="column" gap={4} basis="100%">
              <Flex align="center" gap={2}>
                <Icon />
                <Heading id={headingId} level={3} variant="labelL" contrast="mid">
                  {requirementStatusLabel}
                </Heading>
              </Flex>
              <GroupedApplicationRequirementCards
                requirements={requirementsForStatus}
                selectedRequirementId={selectedRequirementId}
                expandButtonRef={expandButtonRef}
                collapseButtonRef={collapseButtonRef}
                desktopPreviewPanelId={desktopPreviewPanelId}
                handleRequirementCollapse={handleRequirementCollapse}
                setOpenRequirement={setOpenRequirement}
                headingLevel={4}
                onSubmissionSuccess={onSubmissionSuccess}
              />
            </Flex>
          </section>
        );
      })}
    </Flex>
  );
}

type GroupedApplicationRequirementCardsProps = Pick<
  ApplicationRequirementCardProps,
  'desktopPreviewPanelId' | 'expandButtonRef' | 'collapseButtonRef'
> & {
  handleRequirementCollapse: () => void;
  headingLevel?: 3 | 4;
  requirements: ApplicationRequirement[];
  selectedRequirementId?: string;
  setOpenRequirement: (value: ApplicationRequirement | null) => void;
  onSubmissionSuccess: (requirementId: string) => void;
};

function GroupedApplicationRequirementCards({
  collapseButtonRef,
  desktopPreviewPanelId,
  expandButtonRef,
  handleRequirementCollapse,
  headingLevel = 3,
  requirements,
  selectedRequirementId,
  setOpenRequirement,
  onSubmissionSuccess,
}: GroupedApplicationRequirementCardsProps) {
  return (
    <>
      {requirements.map(requirement => (
        <ApplicationRequirementCard
          key={requirement.id}
          selected={requirement.id === selectedRequirementId}
          requirement={requirement}
          hideActions={!!selectedRequirementId}
          expandButtonRef={requirement.id === selectedRequirementId ? expandButtonRef : undefined}
          desktopPreviewPanelId={desktopPreviewPanelId}
          onCollapse={() => handleRequirementCollapse()}
          collapseButtonRef={collapseButtonRef}
          onSubmissionSuccess={onSubmissionSuccess}
          onOpenChange={isOpen => {
            setOpenRequirement(isOpen ? requirement : null);
          }}
          headingLevel={headingLevel}
        />
      ))}
    </>
  );
}

function getRequirementStagesToDisplay() {
  return [
    RequirementApplicationStage.Enum['pre-payment'],
    RequirementApplicationStage.Enum['pre-submission'],
    RequirementApplicationStage.Enum.submission,
    RequirementApplicationStage.Enum['post-submission'],
    RequirementApplicationStage.Enum.admission,
    RequirementApplicationStage.Enum['visa-application'],
    RequirementApplicationStage.Enum['post-decision'],
    RequirementApplicationStage.Enum.deferral,
    RequirementApplicationStage.Enum['pre-arrival'],
    RequirementApplicationStage.Enum.arrival,
    RequirementApplicationStage.Enum.refund,
    RequirementApplicationStage.Enum.general,
  ];
}

function groupRequirementsByStatus({
  openRequirement,
  requirements,
  groupByAttribute,
}: {
  openRequirement: null | Pick<ApplicationRequirement, 'status' | 'applicationStage' | 'id'>;
  requirements: ApplicationRequirement[];
  groupByAttribute: 'status' | 'stage';
}) {
  return groupBy(requirements, requirement => {
    if (groupByAttribute === 'status') {
      const status =
        requirement.id === openRequirement?.id ? openRequirement.status : requirement.status;

      if (status === 'preparing') {
        return 'missing';
      }

      return status;
    }

    return openRequirement?.id ? openRequirement.applicationStage : requirement.applicationStage;
  });
}

function useFilters({
  applicationId,
  applicationRequirements,
  selectedRequirement,
  selectedRequirementId,
}: {
  applicationId: string;
  applicationRequirements: ApplicationRequirement[];
  selectedRequirement?: ApplicationRequirement;
  selectedRequirementId?: string;
}) {
  const history = useHistory();
  const { t } = useTranslation('RequirementList');
  const { permissions } = useApplicationPagePermissions();
  const { featureFlags } = useFeatureFlags();
  const showModernFeatures =
    featureFlags['application_apr_admin_features'] || featureFlags['application_apr_actions_tab'];

  const isAdminRequirementsList = !applicationRequirements.some(
    req => req.classification === 'applicant_requirement',
  );

  const [selectedFilters, setSelectedFilters] = useState<Array<string>>([]);
  const showDeactivated =
    permissions.list_all_requirements || permissions.read_past_stages_optional_requirements;
  const handleFilterChange = (selectedFilters: Array<string>) => {
    setSelectedFilters(selectedFilters);

    const filteredRequirements = filterRequirements(applicationRequirements, selectedFilters);

    // if there was a selection and now the list is empty, reset the selected requirement
    if (selectedRequirement && filteredRequirements.length === 0) {
      history.push(`/applications/${applicationId}${location.search}`);
    }
    if (
      selectedRequirementId &&
      !filteredRequirements.find(req => req.id === selectedRequirementId)
    ) {
      history.push(
        `/applications/${applicationId}/requirements/${filteredRequirements[0].id}${location.search}`,
      );
    }
  };

  const requirementStatuses =
    showModernFeatures && permissions.update_requirement_status
      ? [
          RequirementStatus.Enum.reviewing,
          RequirementStatus.Enum.missing,
          RequirementStatus.Enum.rejected,
          RequirementStatus.Enum.approved,
          ...(showDeactivated ? [RequirementStatus.Enum.inactive] : []),
        ]
      : [
          RequirementStatus.Enum.rejected,
          RequirementStatus.Enum.missing,
          RequirementStatus.Enum.reviewing,
          RequirementStatus.Enum.approved,
        ];

  const requirementStatesOptions = showModernFeatures
    ? [
        RequirementState.Enum.required,
        RequirementState.Enum.optional,
        RequirementState.Enum.earlyAccess,
      ]
    : [];

  const requirementStatusDisplayNames = requirementStatuses.map(status =>
    status === 'missing' && isAdminRequirementsList
      ? t('requirementStatus.missingAdmin')
      : t(`requirementStatus.${status}`),
  );

  const filterOptions = {
    requirementStatus: requirementStatuses,
    requirementState: requirementStatesOptions,
    requirementStatusDisplayNames,
  };

  return {
    selectedFilters,
    handleFilterChange,
    filterOptions,
  };
}

function RequirementFilter({
  filterOptions,
  selectedFilters,
  onChange,
}: {
  filterOptions: {
    requirementStatus: RequirementStatus[];
    requirementState: RequirementState[];
    requirementStatusDisplayNames: string[];
  };
  selectedFilters: Array<string>;
  onChange: (selectedFilters: Array<string>) => void;
}) {
  const { t } = useTranslation('RequirementList');
  const { trackEvent } = useRequirementListEventTracker();
  return (
    <div css={{ minWidth: '161px' }}>
      <MultiSelect
        value={selectedFilters}
        onChange={newValue => {
          trackEvent({
            event: 'Clicked requirement filters',
            data: {
              requirementStatus: newValue.filter(
                value => RequirementStatus.safeParse(value).success,
              ),
              requirementState: newValue.filter(value => RequirementState.safeParse(value).success),
            },
          });
          onChange(newValue);
        }}
        aria-label={`Filters${selectedFilters?.length ? ` (${selectedFilters?.length})` : ''}`}
        placeholderVariant="indicator"
        placeholder={`Filters${selectedFilters?.length ? ` (${selectedFilters?.length})` : ''}`}
        intent="primary"
        emphasis="outlined"
        leadIcon={FilterOutlineIcon}
        menuAlignment="end"
      >
        <MultiSelect.OptionGroup label="Requirement status" value="requirementStatus">
          {filterOptions.requirementStatus.map((value, index) => {
            return (
              <MultiSelect.Option
                key={index.toString()}
                label={filterOptions.requirementStatusDisplayNames[index]}
                value={value}
              />
            );
          })}
        </MultiSelect.OptionGroup>
        {filterOptions.requirementState.length > 0 && (
          <MultiSelect.OptionGroup label="Requirement state" value="requirementState">
            {filterOptions.requirementState.map((value, index) => (
              <MultiSelect.Option
                key={index.toString()}
                label={t(`requirementState.${value}`)}
                value={value}
              />
            ))}
          </MultiSelect.OptionGroup>
        )}
      </MultiSelect>
    </div>
  );
}

function filterRequirements(
  applicationRequirements: ApplicationRequirement[],
  selectedFilters: Array<string>,
) {
  const selectedStatusFilters = requirementStatuses.filter(filter =>
    selectedFilters.includes(filter),
  );
  const selectedStateFilters = requirementStates.filter(filter => selectedFilters.includes(filter));

  const requirementsSortedByOptionality = orderBy(
    applicationRequirements,
    ['flags.optional'],
    ['asc'],
  );

  const filteredRequirementsByStatus = requirementsSortedByOptionality.filter(requirement =>
    selectedStatusFilters.length > 0
      ? selectedStatusFilters.includes(
          requirement.status === 'preparing' ? 'missing' : requirement.status,
        )
      : true,
  );
  const filteredRequirements =
    selectedStateFilters.length > 0
      ? filteredRequirementsByStatus.filter(
          requirement =>
            (!requirement.flags.optional &&
              selectedStateFilters.includes(RequirementState.Enum.required)) ||
            (requirement.flags.optional &&
              selectedStateFilters.includes(RequirementState.Enum.optional)) ||
            (requirement.flags.earlyAccess &&
              selectedStateFilters.includes(RequirementState.Enum.earlyAccess)),
        )
      : filteredRequirementsByStatus;

  return filteredRequirements;
}

function NotFoundModal({ applicationId }: { applicationId: string }) {
  const { t } = useTranslation('RequirementList');
  const location = useLocation();
  const history = useHistory();

  function onOpenChange() {
    history.push(`/applications/${applicationId}${location.search}`);
  }

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <Dialog.Heading>{t('notFoundModalTitle')}</Dialog.Heading>
      <Dialog.Content>
        <Flex gap={4}>
          <DocumentAlertHifiIcon intent="negative" />
          <Text>{t('notFoundModalMessage')}</Text>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Flex grow={1} justify="end" direction={{ xs: 'column', sm: 'row' }}>
          <Dialog.CloseButton emphasis="contained" intent="primary">
            {t('notFoundModalCloseButton')}
          </Dialog.CloseButton>
        </Flex>
      </Dialog.Actions>
    </Dialog>
  );
}

function RequirementGrouping({
  children,
  leadIcon,
  triggerText,
}: {
  children: React.ReactNode;
  leadIcon?: ButtonProps['leadIcon'];
  triggerText: string;
}) {
  const [isOpen, setIsOpen] = useState(true);

  const disclosureControlId = useId();
  const disclosureLabelId = useId();
  const isNewLayoutEnabled = useIsNewLayoutEnabled();

  return (
    <Flex direction="column" gap={2}>
      <Flex.Item align="start">
        <div
          css={theme => ({ marginLeft: isNewLayoutEnabled ? `-${theme.spacing[4]}` : undefined })}
        >
          <Button
            aria-controls={disclosureControlId}
            aria-expanded={isOpen}
            emphasis="transparent"
            id={disclosureLabelId}
            intent="secondary"
            leadIcon={leadIcon}
            trailIcon={isOpen ? ChevronUpOutlineIcon : ChevronDownOutlineIcon}
            onClick={() => setIsOpen(!isOpen)}
          >
            {triggerText}
          </Button>
        </div>
      </Flex.Item>
      <section aria-labelledby={disclosureLabelId} id={disclosureControlId}>
        {isOpen && (
          <Flex direction="column" gap={4}>
            {children}
          </Flex>
        )}
      </section>
    </Flex>
  );
}

function AdminActions({
  filterOptions,
  isCompact,
  onFilterChange,
  onOpenCompareDialog,
  requirementClassifications,
  selectedFilters,
}: {
  filterOptions: ReturnType<typeof useFilters>['filterOptions'];
  isCompact: boolean;
  onFilterChange: (selectedFilters: Array<string>) => void;
  onOpenCompareDialog: () => void;
  requirementClassifications: ApplicationRequirement['classification'][];
  selectedFilters: Array<string>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isConflictingRequirementsOpen, setIsConflictingRequirementsOpen] = useState(false);
  const [isAddRequirementsOpen, setIsAddRequirementsOpen] = useState(false);
  const { t } = useTranslation('RequirementList');
  const { application } = useApplication();
  const { canPerformApplicationAction, getApplicationAvailableAction } =
    useApplicationAvailableActions();
  const { trackEvent } = useRequirementListEventTracker();
  const conflictButtonRef = useRef<HTMLButtonElement | null>(null);
  const actionsButtonRef = useRef<HTMLButtonElement | null>(null);

  const compareRequirementAction = getApplicationAvailableAction(
    ApplicationActionName.COMPARE_REQUIREMENTS,
  );

  const hasConflicts = compareRequirementAction?.additionalInformation?.hasConflicts;
  const canAddRequirements = canPerformApplicationAction(ApplicationActionName.ADD_REQUIREMENTS);
  return (
    <Flex gap={2} grow={1} justify="end">
      {hasConflicts && (
        <Button
          ref={conflictButtonRef}
          leadIcon={WarningOutlineIcon}
          intent="warning"
          emphasis="outlined"
          aria-label={t('adminActions.conflictingRequirementsDialog.button')}
          onClick={() => {
            setIsConflictingRequirementsOpen(true);
            trackEvent({
              event: 'Clicked conflicting requirements',
            });
          }}
        >
          {isCompact ? '' : t('adminActions.conflictingRequirementsDialog.button')}
        </Button>
      )}

      {canAddRequirements && (
        <Flex.Item>
          <Flex gap={2}>
            <AddRequirementsButton
              isOpen={isCompact || hasConflicts ? isAddRequirementsOpen : undefined}
              onOpenChange={
                isCompact || hasConflicts
                  ? isOpen => {
                      setIsAddRequirementsOpen(isOpen);
                    }
                  : undefined
              }
            />
          </Flex>
        </Flex.Item>
      )}
      <RequirementFilter
        filterOptions={filterOptions}
        selectedFilters={selectedFilters}
        onChange={onFilterChange}
      />
      <MenuButton
        alignment="right"
        open={isOpen}
        onOpenChange={newOpen => {
          setIsOpen(newOpen);
          if (newOpen) {
            trackEvent({ event: 'Clicked three dot menu' });
          }
        }}
        menuWidth="hug"
      >
        <MenuButton.Trigger>
          <Button
            ref={actionsButtonRef}
            leadIcon={MoreVertOutlineIcon}
            emphasis="outlined"
            intent="primary"
            aria-label={t('adminActions.additionalActions')}
            onClick={() => {
              setIsOpen(true);
            }}
          />
        </MenuButton.Trigger>
        {canAddRequirements && (isCompact || hasConflicts) && (
          <MenuButton.Button
            icon={PlusOutlineIcon}
            id="addRequirements"
            onClick={() => {
              setIsAddRequirementsOpen(true);
              trackEvent({
                event: 'Clicked add requirements',
              });
            }}
          >
            {t('adminActions.addRequirements')}
          </MenuButton.Button>
        )}
        {compareRequirementAction && (
          <MenuButton.Button
            icon={CompareArrowsOutlineIcon}
            id="compareRequirements"
            onClick={() => {
              onOpenCompareDialog();
              setIsOpen(false);
              trackEvent({ event: 'Three dot menu - Clicked compare requirements' });
            }}
          >
            {t('adminActions.compareRequirements')}
          </MenuButton.Button>
        )}
        <MenuButton.Link
          icon={DownloadOutlineIcon}
          id="downloadAllApprovedDocuments"
          href={`${window.location.origin}/awa/v1/media/requirement_attachments?application_id=${application.id}&filter[decisions]=approved&filter[application_requirement_classification]=${requirementClassifications.join(',')}`}
          onClick={() =>
            trackEvent({
              event: 'Three dot menu - Clicked download approved documents',
            })
          }
        >
          {t('adminActions.downloadAllApprovedDocuments')}
        </MenuButton.Link>
      </MenuButton>
      <ConflictingRequirementsDialog
        isOpen={isConflictingRequirementsOpen}
        onClose={() => {
          flushSync(() => {
            setIsConflictingRequirementsOpen(false);
          });
          if (conflictButtonRef.current) {
            conflictButtonRef.current.focus();
          } else {
            actionsButtonRef.current?.focus();
          }
        }}
        onOpenCompareDialog={onOpenCompareDialog}
      />
    </Flex>
  );
}

function ConflictingRequirementsDialog({
  isOpen,
  onClose,
  onOpenCompareDialog,
}: {
  isOpen: boolean;
  onClose: () => void;
  onOpenCompareDialog: () => void;
}) {
  const { t } = useTranslation('RequirementList');
  const { dismissManualInspection, isDismissingManualInspection } = useDismissManualInspection();
  const toasts = useToasts();
  return (
    <Dialog
      open={isOpen}
      size="lg"
      onOpenChange={newOpen => {
        if (!newOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Heading>{t('adminActions.conflictingRequirementsDialog.title')}</Dialog.Heading>
      <Dialog.Content>
        <Flex gap={4}>
          <WarningOutlineIcon size="xl" intent="warning" />
          <Flex direction="column" gap={3} align="center">
            <Text>{t('adminActions.conflictingRequirementsDialog.info')}</Text>
            <Text>{t('adminActions.conflictingRequirementsDialog.infoContinued')}</Text>
          </Flex>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Flex gap={3} justify="between" grow={1}>
          <Dialog.CloseButton>
            {t('adminActions.conflictingRequirementsDialog.close')}
          </Dialog.CloseButton>
          <Flex gap={3} grow={1} justify="end">
            <Button
              onClick={() => {
                onClose();
                onOpenCompareDialog();
              }}
              leadIcon={CompareArrowsOutlineIcon}
              intent="primary"
              emphasis="outlined"
            >
              {t('adminActions.conflictingRequirementsDialog.compareRequirements')}
            </Button>
            <Button
              onClick={() => {
                dismissManualInspection(
                  {},
                  {
                    onSuccess: () => {
                      toasts.positive(t('adminActions.conflictingRequirementsDialog.success'));
                      onClose();
                    },
                    onError: error => {
                      if (error instanceof Error) {
                        toasts.negative(error);
                      }
                    },
                  },
                );
              }}
              leadIcon={DocumentCheckOutlineIcon}
              loading={isDismissingManualInspection}
              intent="warning"
              emphasis="outlined"
            >
              {t('adminActions.conflictingRequirementsDialog.dismissWarning')}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Actions>
    </Dialog>
  );
}
